/**
 * Centralized React Query configuration
 * All timing values can be controlled via environment variables
 */
import { getRuntimeEnv } from '../../Utils/runtimeEnv';

/**
 * Helper to parse and validate millisecond values from environment variables
 * @param value - Environment variable value
 * @param fallback - Default value to use if parsing fails
 * @returns Parsed millisecond value or fallback
 */
const parseMs = (value: string | undefined, fallback: number): number => {
  if (!value) return fallback;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) || parsed < 0 ? fallback : parsed;
};

/**
 * Centralized query configuration object
 * Maps feature-specific query timing to environment variables
 */
export const QUERY_CONFIG = {
  // Global defaults for all queries
  DEFAULT_STALE_TIME: parseMs(
    getRuntimeEnv('REACT_APP_QUERY_STALE_TIME'),
    10 * 60 * 1000
  ), // 10 minutes
  DEFAULT_GC_TIME: parseMs(
    getRuntimeEnv('REACT_APP_QUERY_GC_TIME'),
    15 * 60 * 1000
  ), // 10 minutes

  // Chat - Real-time features
  CHAT: {
    staleTime: parseMs(getRuntimeEnv('REACT_APP_QUERY_CHAT_STALE_TIME'), 0), // Always fresh
    refetchInterval: parseMs(
      getRuntimeEnv('REACT_APP_QUERY_CHAT_REFETCH'),
      5000
    ), // Poll every 5 seconds
  },

  // Dashboard - Moderate refresh rate
  DASHBOARD: {
    staleTime: parseMs(
      getRuntimeEnv('REACT_APP_QUERY_DASHBOARD_STALE_TIME'),
      10 * 60 * 1000
    ), // 10 minutes
    refetchInterval: parseMs(
      getRuntimeEnv('REACT_APP_QUERY_DASHBOARD_REFETCH'),
      10 * 60 * 1000
    ), // 10 minutes
  },

  // User & Permissions - Cached with periodic refresh
  USER_PERMISSIONS: {
    staleTime: parseMs(
      getRuntimeEnv('REACT_APP_QUERY_USER_STALE_TIME'),
      15 * 60 * 1000
    ), // 15 minutes
    refetchInterval: parseMs(
      getRuntimeEnv('REACT_APP_QUERY_USER_REFETCH'),
      15 * 60 * 1000
    ), // 15 minutes
  },

  // Notifications - Short cache time
  NOTIFICATIONS: {
    staleTime: parseMs(
      getRuntimeEnv('REACT_APP_QUERY_NOTIFICATIONS_STALE_TIME'),
      30 * 1000
    ), // 30 seconds
    refetchInterval: parseMs(
      getRuntimeEnv('REACT_APP_QUERY_NOTIFICATIONS_REFETCH'),
      0
    ), // Disabled by default (0)
  },

  // Reports - Poll when pending operations exist
  REPORTS_PENDING: {
    refetchInterval: parseMs(
      getRuntimeEnv('REACT_APP_QUERY_REPORTS_PENDING_REFETCH'),
      30000
    ), // 30 seconds
  },

  // Lists - Long cache time for list views
  LISTS: {
    staleTime: parseMs(
      getRuntimeEnv('REACT_APP_QUERY_LISTS_STALE_TIME'),
      10 * 60 * 1000
    ), // 10 minutes
  },
} as const;

// Export individual configs for backward compatibility and convenience
export const DEFAULT_STALE_TIME = QUERY_CONFIG.DEFAULT_STALE_TIME;
export const DEFAULT_GC_TIME = QUERY_CONFIG.DEFAULT_GC_TIME;
